/** venture/KitRow.tsx — 키트 항목 1행: 앞단계 연동 / 직접 입력·업로드 (9단계 구비서류 행과 같은 문법, 2026-09-24)
 *  kind: text(한 줄) · memo(여러 줄, 투자자 인적사항) · members(조합원 인적사항 — 마스터 자동 + 출자증표 열기) · file */
import React from 'react';
import { getUpKey, mutStore, UpLabel, renameUpload, dispName } from './stepcommon';
import { getVers } from './gendocs';
import { getBondVers } from './bonddoc';
import { s3upload, uploadPreview } from './api';
import { openS3, downloadUploadFile } from './docgen';
import { kitKey } from './certplan';
import BondDocRow from './BondDocRow';
import type { KitRowState } from './kit13';
import type { Project, CoCard, CertMode } from './types';

interface Props {
  p: Project; co: CoCard; mode: CertMode | ''; row: KitRowState;
  onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void;
}

export default function KitRow({ p, co, mode, row, onPatch, toast }: Props) {
  const { item, linked, src, own, res } = row;
  const key = kitKey(co.id, mode, item.slot);
  const hasAuto = !!item.from || item.kind === 'members' || !!res;
  const setSrc = (v: 'link' | 'manual') => onPatch(mutStore(p, 's13', (s) => { s.src = s.src || {}; s.src[key] = v; }));
  const setTxt = (v: string) => onPatch(mutStore(p, 's13', (s) => { s.txt = s.txt || {}; s.txt[key] = v; }));
  const upload = async (f0: File) => {
    try {
      const r = await s3upload(p.id, renameUpload(f0, item.label, co.name || '법인'));   // 「항목명_법인명.확장자」로 정돈
      onPatch(mutStore(p, 's13', (st) => { st.up[key] = r.name; st.upKey = st.upKey || {}; st.upKey[key] = r.key; }));
      toast('업로드됨 · ' + r.name);
    } catch (e) { toast((e as Error).message); }
  };
  const removeUp = () => onPatch(mutStore(p, 's13', (st) => { if (st.up) delete st.up[key]; if (st.upKey) delete st.upKey[key]; }));
  const openDoc = async (docKey: string, label: string) => {
    const vers = getVers(p, docKey);
    const v = vers[vers.length - 1];
    const k = v && (v.s3pdf || v.s3doc);
    if (!k) { toast(label + ' 생성본이 없습니다 — 7단계에서 먼저 생성해 주세요'); return; }
    try { await openS3(k, v.pdf || v.doc, true); } catch (e) { toast((e as Error).message); }
  };
  // 연동 파일 열기 — 앞단계 업로드(S3 키) · 생성 문서(docver) · 자료수집 포털 제출물(uploadId)
  const openLinked = async () => {
    const f = item.from;
    try {
      if (res && res.uploadId) {   // kitRow 는 앞단계 연동값이 없을 때만 포털 제출물을 res 로 준다
        const r = await uploadPreview(res.uploadId);
        window.open(r.url, '_blank', 'noopener');
        return;
      }
      if (!f) return;
      if (f.kind === 'file' && f.store && f.key) {
        const k = getUpKey(p, f.store, f.key);
        if (!k) { toast('원본 키가 없습니다 — 앞단계에서 다시 업로드해 주세요'); return; }
        await openS3(k, linked, true);
      } else if (f.kind === 'doc') {
        const vers = f.bond ? getBondVers(p, co, f.bond) : getVers(p, f.docKey || '');
        const v = vers[vers.length - 1];
        const k = v && (v.s3pdf || v.s3doc);
        if (!k) { toast('생성본이 없습니다 — 해당 단계에서 먼저 생성해 주세요'); return; }
        await openS3(k, v.pdf || v.doc, true);
      }
    } catch (e) { toast((e as Error).message); }
  };
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast('복사했습니다'); } catch { toast('복사 실패 — 직접 선택해 복사해 주세요'); }
  };

  const autoLabel = res ? '자료수집 제출' : item.from ? item.from.label : '마스터 자동';
  const sel = hasAuto ? (
    <select className="srcsel" value={src} onChange={(e) => setSrc(e.target.value === 'link' ? 'link' : 'manual')}>
      <option value="link">{autoLabel}</option>
      <option value="manual">{item.kind === 'file' ? '직접 업로드' : '직접 입력'}</option>
    </select>
  ) : null;
  const linkChip = item.link ? <a className="upbtn link" href={item.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10.5, padding: '3px 8px' }}>🔗 {item.link.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a> : null;

  let body: React.ReactNode;
  const isFileLink = res ? res.type === 'file' : (item.from && item.from.kind !== 'text' && item.from.kind !== 'collect' && item.from.kind !== 'master');
  const bondDoc = item.from && item.from.kind === 'doc' && item.from.bond ? item.from.bond : '';
  if (hasAuto && src === 'link' && bondDoc) {
    // 계약서: 생성 → 미리보기 → ✍ 서명(드로잉패드) → 날인본 저장이 한 행에서 — 서명본이 있어야 완료(✓)
    body = (
      <div className="kitdoc">
        {!linked ? <div className="snote" style={{ marginBottom: 4 }}>서명본이 아직 없습니다 — 아래에서 생성 후 「✍ 서명」 하거나 날인본(PDF)을 올려 주세요</div> : null}
        <BondDocRow p={p} co={co} k={bondDoc} onPatch={onPatch} toast={toast} />
      </div>
    );
  } else if (hasAuto && src === 'link') {
    if (!linked) {
      body = <div className="s3file"><span className="s3muted">앞단계 값이 없습니다 · &apos;직접 {item.kind === 'file' ? '업로드' : '입력'}&apos;으로 전환하거나 해당 단계에서 채워 주세요</span></div>;
    } else if (item.kind === 'members') {
      body = (
        <div className="s3file" style={{ alignItems: 'flex-start' }}>
          <pre className="kitsum-pre" style={{ flex: 1, margin: 0 }}>{linked}</pre>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button className="rbtn" onClick={() => openDoc('jeungpyo', '출자증표')}>📕 출자증표 열기</button>
            <button className="rbtn" onClick={() => openDoc('roster', '조합원명부')}>📕 명부 열기</button>
            <button className="rbtn ok" onClick={() => copy(linked)}>복사</button>
          </span>
        </div>
      );
    } else if (isFileLink) {
      body = <div className="s3file"><span className="rf">📄 {linked}</span><span className="pill p-blue">{autoLabel}</span><span className="grow" /><button className="rbtn" onClick={openLinked}>👁 보기</button></div>;
    } else {
      body = <div className="s3file"><span className="rf">{linked}</span><span className="pill p-blue">{autoLabel}</span><span className="grow" /><button className="rbtn" onClick={() => copy(linked)}>복사</button></div>;
    }
  } else if (item.kind === 'text') {
    body = <input className="cotext" value={own} onChange={(e) => setTxt(e.target.value)} placeholder={item.hint || item.label} />;
  } else if (item.kind === 'memo' || item.kind === 'members') {
    body = <textarea className="cotext" rows={3} value={own} onChange={(e) => setTxt(e.target.value)} placeholder={item.hint || '한 사람에 한 줄'} />;
  } else {
    body = own ? (
      <div className="s3file ok"><span className="rf">📄 {own}</span><span className="pill p-green">업로드됨</span><span className="grow" />
        <button className="rbtn" onClick={() => openS3(getUpKey(p, 's13', key), own, true).catch((e) => toast((e as Error).message))}>👁 보기</button>
        <button className="rbtn" onClick={() => downloadUploadFile(p, getUpKey(p, 's13', key), dispName(own, item.label, co.name || '법인'), toast)}>⬇</button>
        <UpLabel text="다시" onUp={upload} />
        <button className="rbtn danger" onClick={removeUp}>🗑</button></div>
    ) : <UpLabel text={'⬆ 업로드 (또는 드래그)' + (item.hint ? ' · ' + item.hint : '')} onUp={upload} />;
  }
  return (
    <div className="codoc">
      <div className="codoc-h"><span>{row.done ? '✓ ' : ''}{item.label}{linkChip ? <span style={{ marginLeft: 6 }}>{linkChip}</span> : null}</span>{sel}</div>
      {body}
    </div>
  );
}
